import React from "react";
import type { Professional } from "../../../services/professionalsApi";
import { DataList, ListColumn, ListAction, Avatar } from "../../shared";
import { Icons } from "../../shared/Icons";
import { displayPhoneNumber } from "../../../utils/phoneUtils";

interface ProfessionalsListViewProps {
  professionals: Professional[];
  onEdit: (professional: Professional) => void;
  onDelete?: (professionalId: number) => void;
  onManageAvailability: (professional: Professional) => void;
  onResetPassword: (professional: Professional) => void;
  loading?: boolean;
  companyCategory?: "car" | "barbershop";
  isProfessional?: boolean;
}

export const ProfessionalsListView: React.FC<ProfessionalsListViewProps> = ({
  professionals,
  onEdit,
  onDelete,
  onManageAvailability,
  onResetPassword,
  loading = false,
  companyCategory,
  isProfessional = false,
}) => {
  const columns: ListColumn<Professional>[] = [
    {
      key: "name",
      label: "Profissional",
      width: "3fr",
      render: (professional) => (
          <div className="flex items-center">
            <Avatar
              text={professional.name}
              src={professional.picture}
              size="md"
              variant="blue"
            />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">
                {professional.name}
              </p>
            </div>
          </div>
        ),
    },
    {
      key: "email",
      label: "E-mail",
      width: "2fr",
      render: (professional) => (
        <span className="text-sm text-gray-900">
          {professional.email || "Não informado"}
        </span>
      ),
    },
    {
      key: "phoneNumber",
      label: "Telefone",
      width: "1.5fr",
      render: (professional) => (
        <span className="text-sm text-gray-900">
          {professional.phoneNumber
            ? displayPhoneNumber(professional.phoneNumber)
            : "Não informado"}
        </span>
      ),
    },
    ...(companyCategory === "barbershop"
      ? [
          {
            key: "availability",
            label: "Disponibilidade",
            width: "1.5fr",
            render: (professional: Professional) => (
              <button
                onClick={() => onManageAvailability(professional)}
                className="text-sm font-semibold text-purple-600 bg-purple-50 hover:bg-purple-100 px-3 py-1 rounded-lg transition-colors duration-200"
              >
                Gerenciar
              </button>
            ),
          },
        ]
      : []),
  ];

  const actions: ListAction<Professional>[] = [
    {
      label: "Editar profissional",
      icon: <Icons.Edit sx={{ fontSize: 18 }} />,
      onClick: (professional) => onEdit(professional),
      className:
        "p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200",
      title: "Editar profissional",
      disabled: () => loading,
    },
    {
      label: "Alterar senha",
      icon: <Icons.Lock sx={{ fontSize: 18 }} />,
      onClick: (professional) => onResetPassword(professional),
      className:
        "p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200",
      title: "Alterar senha",
      disabled: () => loading,
    },
    ...(onDelete && !isProfessional
      ? [
          {
            label: "Excluir profissional",
            icon: <Icons.Delete sx={{ fontSize: 18 }} />,
            onClick: (professional: Professional) => onDelete(professional.id),
            className:
              "p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200",
            title: "Excluir profissional",
            disabled: () => loading,
          } as ListAction<Professional>,
        ]
      : []),
  ];

  return (
    <>
      <DataList<Professional>
        data={professionals}
        columns={columns}
        actions={actions}
        keyExtractor={(professional) => professional.id}
        emptyMessage="Nenhum profissional encontrado"
        loading={loading}
        itemLabel={
          professionals.length === 1 ? "profissional" : "profissionais"
        }
        onRowClick={(professional) => onEdit(professional)}
      />
    </>
  );
};
